/**
 * security/securityConfig.ts — BETA GATE HARDENING™ sprint (Critical Issue #2).
 *
 * Fail closed. Security enforcement is ON by default in EVERY environment.
 * The ONLY way to disable it is an explicit development configuration
 * (VITE_ENFORCE_SECURITY=false) — never hardcoded, never silent (the boot log
 * states mode, environment, and configuration source). Production ignores the
 * override entirely: it always enforces.
 *
 * Pure function over an injectable env snapshot so tests can exercise every
 * branch without touching import.meta.
 */

export interface SecurityEnvSnapshot {
  PROD?: boolean;
  MODE?: string;
  VITE_ENFORCE_SECURITY?: string;
}

export type SecurityConfigSource =
  | 'production-default'      // production build: always enforced, override ignored
  | 'dev-env-override'        // development: VITE_ENFORCE_SECURITY explicitly set
  | 'secure-default';         // development: no override → enforced (fail closed)

export interface SecurityResolution {
  enforceSecurity: boolean;
  environment: 'production' | 'development';
  source: SecurityConfigSource;
  /** True when a dev override tried to disable security in production (ignored + warned). */
  ignoredInsecureOverride: boolean;
}

export function resolveSecurityConfig(env?: SecurityEnvSnapshot): SecurityResolution {
  const e: SecurityEnvSnapshot = env ?? (import.meta.env as unknown as SecurityEnvSnapshot);
  const isProduction = Boolean(e?.PROD) || e?.MODE === 'production';

  if (isProduction) {
    return {
      enforceSecurity: true, // ALWAYS — no production insecure defaults, period
      environment: 'production',
      source: 'production-default',
      ignoredInsecureOverride: e?.VITE_ENFORCE_SECURITY === 'false',
    };
  }
  if (e?.VITE_ENFORCE_SECURITY != null && e.VITE_ENFORCE_SECURITY !== '') {
    return {
      enforceSecurity: e.VITE_ENFORCE_SECURITY !== 'false',
      environment: 'development',
      source: 'dev-env-override',
      ignoredInsecureOverride: false,
    };
  }
  return {
    enforceSecurity: true, // secure by default even in development
    environment: 'development',
    source: 'secure-default',
    ignoredInsecureOverride: false,
  };
}

/**
 * The explicit permission grant for the local single-operator platform.
 * WorkRoamer OS is local-first with no auth backend yet: enforcement is real
 * (fail closed), and this session is the recorded identity performing
 * privileged core operations. Anything OUTSIDE this list is denied. When a
 * real auth provider lands, it replaces this session behind the same
 * SecurityContext surface.
 */
export const LOCAL_OPERATOR_PERMISSIONS = [
  'core.engines.manage',
  'core.workflows.run',
  'core.jobs.run',
] as const;

export function localOperatorSession(now: number) {
  return {
    sessionId: `local-operator-${now}`,
    userId: 'local-operator',
    expiresAt: now + 365 * 24 * 60 * 60 * 1000, // revalidated on real-auth arrival
    permissions: [...LOCAL_OPERATOR_PERMISSIONS],
  };
}
